import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Icons } from '../shared/Icons'
import { useAuth } from '../../context/AuthContext'
import './Navbar.css'

const navLinks = [
  { path: '/features', label: 'Features' },
  { path: '/platform', label: 'Platform' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/blog', label: 'Blog' },
  { path: '/about', label: 'About' },
  { path: '/support', label: 'Support' }
]

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const userMenuRef = useRef(null)

  const { user, isAuthenticated, loading, logout } = useAuth()

  // Homepage has a light hero, other pages have dark heroes
  const isHomePage = location.pathname === '/'

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsUserMenuOpen(false)
  }, [location])

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    await logout()
    setIsUserMenuOpen(false)
    navigate('/')
  }

  const getUserInitials = () => {
    if (!user) return '?'
    const first = user.first_name?.[0] || user.email?.[0] || ''
    const last = user.last_name?.[0] || ''
    return (first + last).toUpperCase() || '?'
  }

  return (
    <motion.header
      className={`navbar ${isScrolled ? 'navbar-scrolled' : ''} ${isHomePage && !isScrolled ? 'navbar-light-hero' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <nav className="navbar-container">
        <Link to="/" className="navbar-logo">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="14" stroke="url(#logo-gradient)" strokeWidth="2" />
            <circle cx="16" cy="16" r="8" fill="url(#logo-gradient)" />
            <defs>
              <linearGradient id="logo-gradient" x1="0" y1="0" x2="32" y2="32">
                <stop stopColor="#FFD700" />
                <stop offset="1" stopColor="#00FF88" />
              </linearGradient>
            </defs>
          </svg>
          <span className="navbar-logo-text">AI4Solar</span>
        </Link>

        <ul className="navbar-links">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`navbar-link ${location.pathname === link.path ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="navbar-actions">
          <div className="navbar-app-badges">
            <a href="#" className="navbar-app-badge navbar-app-badge-coming-soon" aria-label="iOS App Coming Soon" title="iOS App Coming Soon">
              <Icons.apple size={16} />
              <span>iOS</span>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.ai4solar.app" className="navbar-app-badge" aria-label="Get it on Google Play" target="_blank" rel="noopener noreferrer">
              <Icons.playStore size={16} />
              <span>Android</span>
            </a>
          </div>

          {/* Auth Section */}
          {!loading && (
            <div className="navbar-auth">
              {isAuthenticated ? (
                <div className="user-menu-wrapper" ref={userMenuRef}>
                  <button
                    className="user-menu-trigger"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    aria-label="User menu"
                  >
                    <span className="user-avatar">{getUserInitials()}</span>
                    <Icons.chevronDown size={16} className={isUserMenuOpen ? 'rotated' : ''} />
                  </button>

                  <AnimatePresence>
                    {isUserMenuOpen && (
                      <motion.div
                        className="user-menu-dropdown"
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                      >
                        <div className="user-menu-header">
                          <span className="user-name">
                            {user?.first_name} {user?.last_name}
                          </span>
                          <span className="user-email">{user?.email}</span>
                        </div>
                        <div className="user-menu-divider" />
                        <a
                          href="https://app.ai4solar.com"
                          className="user-menu-item"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Icons.activity size={18} />
                          Dashboard
                        </a>
                        <Link to="/pricing" className="user-menu-item">
                          <Icons.package size={18} />
                          Subscription
                        </Link>
                        <div className="user-menu-divider" />
                        <button className="user-menu-item logout" onClick={handleLogout}>
                          <Icons.logOut size={18} />
                          Sign Out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link to="/signin" className="navbar-signin-btn">
                  Sign In
                </Link>
              )}
            </div>
          )}
        </div>

        <button
          className="navbar-mobile-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="navbar-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ul>
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`navbar-mobile-link ${location.pathname === link.path ? 'active' : ''}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}

              {/* Mobile Auth Links */}
              {!loading && (
                <li className="mobile-auth-section">
                  {isAuthenticated ? (
                    <>
                      <div className="mobile-user-info">
                        <span className="user-avatar">{getUserInitials()}</span>
                        <span>{user?.first_name} {user?.last_name}</span>
                      </div>
                      <a
                        href="https://app.ai4solar.com"
                        className="navbar-mobile-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Dashboard
                      </a>
                      <button className="navbar-mobile-link logout-btn" onClick={handleLogout}>
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/signin" className="navbar-mobile-link signin-link">
                        Sign In
                      </Link>
                      <Link to="/register" className="navbar-mobile-link register-link">
                        Create Account
                      </Link>
                    </>
                  )}
                </li>
              )}

              <li className="mobile-app-badges">
                <a href="#" className="navbar-app-badge navbar-app-badge-coming-soon" aria-label="iOS App Coming Soon" title="iOS App Coming Soon">
                  <Icons.apple size={18} />
                  <span>iOS App</span>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.ai4solar.app" className="navbar-app-badge" aria-label="Get it on Google Play" target="_blank" rel="noopener noreferrer">
                  <Icons.playStore size={18} />
                  <span>Android App</span>
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}
